import request from '@/config/axios'
import type { PageParam } from '@/types/common'
import type { PageResult } from '@/api/lawyer'

export interface FirmReceivableVO {
  id?: number
  caseId: number
  caseTitle?: string
  firmCaseNo?: string
  customerId?: number
  customerName?: string
  feeType: string
  title?: string
  amount: number
  receivedAmount?: number
  outstandingAmount?: number
  dueDate?: string
  status?: string
  overdueFlag?: boolean
  overdueSince?: string
  remark?: string
  createTime?: string
}

export interface FirmReceivablePageReqVO extends PageParam {
  caseId?: number
  customerId?: number
  status?: string
  overdueOnly?: boolean
}

export interface FirmReceivableSummaryVO {
  totalAmount: number
  receivedAmount: number
  outstandingAmount: number
  overdueCount: number
  overdueAmount: number
}

export interface FirmReceiptVO {
  id?: number
  caseId: number
  caseTitle?: string
  firmCaseNo?: string
  receivableId: number
  receivableTitle?: string
  amount: number
  payTime: string
  payMethod?: string
  voucherUrl?: string
  status?: string
  voidReason?: string
  remark?: string
  createTime?: string
}

export interface FirmReceiptPageReqVO extends PageParam {
  caseId?: number
  receivableId?: number
  status?: string
}

const AR_STATUS_OPTIONS = [
  { value: 'DRAFT', label: '草稿' },
  { value: 'OPEN', label: '未收' },
  { value: 'PARTIAL', label: '部分已收' },
  { value: 'CLEARED', label: '已收清' },
  { value: 'VOID', label: '作废' }
] as const

const FEE_TYPE_OPTIONS = [
  { value: 'DEPOSIT', label: '首期/定金' },
  { value: 'MID', label: '中期' },
  { value: 'FINAL', label: '尾款' },
  { value: 'CONTINGENT', label: '风险代理' },
  { value: 'OTHER', label: '其他' }
] as const

const PAY_METHOD_OPTIONS = [
  { value: 'CASH', label: '现金' },
  { value: 'TRANSFER', label: '转账' },
  { value: 'WECHAT', label: '微信' },
  { value: 'ALIPAY', label: '支付宝' },
  { value: 'OTHER', label: '其他' }
] as const

export function arStatusLabel(code?: string) {
  return AR_STATUS_OPTIONS.find((o) => o.value === code)?.label || code || '-'
}

export function feeTypeLabel(code?: string) {
  return FEE_TYPE_OPTIONS.find((o) => o.value === code)?.label || code || '-'
}

export const FirmReceivableApi = {
  create: (data: Partial<FirmReceivableVO>) =>
    request.post({ url: '/lawyer/firm/receivable/create', data }),
  update: (data: Partial<FirmReceivableVO>) =>
    request.put({ url: '/lawyer/firm/receivable/update', data }),
  delete: (id: number) => request.delete({ url: '/lawyer/firm/receivable/delete', params: { id } }),
  get: (id: number) =>
    request.get({ url: '/lawyer/firm/receivable/get', params: { id } }) as Promise<FirmReceivableVO>,
  page: (params: FirmReceivablePageReqVO) =>
    request.get({ url: '/lawyer/firm/receivable/page', params }) as Promise<PageResult<FirmReceivableVO>>,
  open: (id: number) => request.put({ url: '/lawyer/firm/receivable/open', data: { id } }),
  void: (data: { id: number; reason: string }) =>
    request.put({ url: '/lawyer/firm/receivable/void', data }),
  refreshOverdue: () => request.post({ url: '/lawyer/firm/receivable/refresh-overdue' }),
  summary: (overdueOnly?: boolean) =>
    request.get({
      url: '/lawyer/firm/receivable/summary',
      params: { overdueOnly }
    }) as Promise<FirmReceivableSummaryVO>
}

export const FirmReceiptApi = {
  create: (data: Partial<FirmReceiptVO>) => request.post({ url: '/lawyer/firm/receipt/create', data }),
  get: (id: number) =>
    request.get({ url: '/lawyer/firm/receipt/get', params: { id } }) as Promise<FirmReceiptVO>,
  page: (params: FirmReceiptPageReqVO) =>
    request.get({ url: '/lawyer/firm/receipt/page', params }) as Promise<PageResult<FirmReceiptVO>>,
  void: (data: { id: number; reason: string }) => request.put({ url: '/lawyer/firm/receipt/void', data })
}

export { AR_STATUS_OPTIONS, FEE_TYPE_OPTIONS, PAY_METHOD_OPTIONS }
